/**
 * Single source of truth for the macro names a COU publisher template may resolve.
 *
 * The offer + campaign namespace is filtered through `isAllowedCouMacro` before it
 * reaches `injectMacros`, so an unlisted key resolves to empty string instead of
 * exposing MomentScience-internal campaign data (`internal_offer_name`,
 * `advertiser_id`, `capping_config`, `status`, …). See GitHub issue #55.
 *
 * The dashboard COU preview (`platform` `src/helpers/cou.helper.ts`) must consume
 * this same list — two independent copies drift, which is how the escaping
 * mismatch between SDK and preview happened.
 *
 * Deliberately NOT gated by this module: `sdk.userAttributes` and the
 * `styles.header` strings. Those are the publisher's own values, so they carry
 * nothing internal to leak.
 */
/** Text macros — double brace, HTML-escaped: `{{title}}`. */
export const COU_TEXT_MACROS = [
    'title',
    'description',
    'short_headline',
    'short_description',
    'advertiser_name',
    'cta_yes',
    'cta_no',
    'lead_in',
    'header_text',
    'terms_and_conditions',
];
/** Image / URL macros — triple brace so `&` in the URL stays raw: `{{{hero_image}}}`. */
export const COU_IMAGE_MACROS = [
    'image',
    'hero_image',
    'icon_image',
    'offer_image',
    'publisher_image',
];
/**
 * Publisher header copy from `styles.header` (see `publisherHeaderMacros`).
 * `lead_in` / `header_text` are the documented aliases and live in
 * `COU_TEXT_MACROS`; these are the remaining publisher-facing keys that layer
 * populates.
 */
export const COU_PUBLISHER_HEADER_MACROS = [
    'text',
    'lead_in_text',
    'eyebrow',
    'subhead',
];
/**
 * Interaction tokens — not brace macros but `data-ms-action` attribute values, the
 * way a theme builder places an interactive control in a custom layout. Handled by
 * the delegated click listener in `CustomOfferUnit`, and, like the macro lists
 * above, this is the list the dashboard's token picker must mirror.
 */
export const COU_ACTION_TOKENS = [
    'cta',
    'no-thanks',
    'close',
    'next',
    'prev',
    'offerwall',
    'save-for-later',
];
/** Attribute selector for one action token — keeps the attribute name in one place. */
export function couActionSelector(token) {
    return `[data-ms-action="${token}"]`;
}
/**
 * Marks a container whose contents the SDK does not manage: the morph leaves its
 * subtree alone (`domMorph`) and the script walk does not descend into it
 * (`scriptRehydrate`). One marker, one guarantee, honoured by both.
 *
 * It lives here rather than in either of those modules because it is part of the
 * same template vocabulary as the action tokens above — the dashboard has to
 * offer it — and because a shared leaf module keeps the two consumers from
 * importing each other.
 */
export const COU_PRESERVE_ATTRIBUTE = 'data-ms-preserve';
/**
 * `publisher_image_N` is generated one per `campaign.publisher_campaign_images`
 * entry and is unbounded, so it needs a pattern rule rather than literal entries.
 */
export const COU_MACRO_NAME_PATTERNS = [
    /^publisher_image_\d+$/,
];
/** Every literal macro name a template may resolve (patterns handled separately). */
export const COU_ALLOWED_MACRO_NAMES = new Set([
    ...COU_TEXT_MACROS,
    ...COU_IMAGE_MACROS,
    ...COU_PUBLISHER_HEADER_MACROS,
]);
/** True when `name` is a macro a publisher template is allowed to resolve. */
export function isAllowedCouMacro(name) {
    if (COU_ALLOWED_MACRO_NAMES.has(name)) {
        return true;
    }
    return COU_MACRO_NAME_PATTERNS.some(pattern => pattern.test(name));
}
/**
 * Drops every non-allowlisted key so `injectMacros` never sees it. Unknown keys
 * then resolve exactly like an absent one — empty string, no literal `{{key}}`
 * left on the page — because the resolver itself is unchanged.
 */
export function filterToAllowedCouMacros(attrs) {
    const out = {};
    for (const [key, value] of Object.entries(attrs)) {
        if (isAllowedCouMacro(key)) {
            out[key] = value;
        }
    }
    return out;
}
